<template>
  <div v-show="products?.length || loading" :class="{ 'pb-3 lg:pb-6': selectedVariant !== 'search-hub' }">
    <div
      v-if="title"
      class="mx-4 flex items-center pb-2 between"
      :class="selectedVariant === 'search-hub' ? 'pt-2' : 'pt-6 lg:mx-6 lg:pb-4'"
    >
      <h2 v-style:c="titleColor" :class="selectedVariant === 'search-hub' ? 'subtitle-5 c-brand-1' : titleStyle">
        <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
        <span v-else>{{ title }}</span>
      </h2>
      <div v-if="carouselRef?.carousel" class="flex gap-1 <lg:hidden" :class="{ 'lg:hidden': selectedVariant === 'search-hub' }">
        <base-button
          :aria-label="$t.previous"
          class="rounded-xs p-1 duration"
          :class="carouselRef.carousel.canScrollPrev ? '@hactive:bg-brand-1/10' : 'c-grey-60'"
          :disabled="!carouselRef.carousel.canScrollPrev"
          @click="carouselRef?.scrollPrev()"
        >
          <vf-icon dir="left" name="chevron" size="md" />
        </base-button>
        <base-button
          :aria-label="$t.next"
          class="rounded-xs p-1 duration"
          :class="carouselRef.carousel.canScrollNext ? '@hactive:bg-brand-1/10' : 'c-grey-60'"
          :disabled="!carouselRef.carousel.canScrollNext"
          @click="carouselRef?.scrollNext()"
        >
          <vf-icon dir="right" name="chevron" size="md" />
        </base-button>
      </div>
    </div>
    <vf-carousel-elevate
      ref="carouselRef"
      align="start"
      :class-container="brandClasses.carouselContainer"
      :class-controls="`${brandClasses.carouselControls} ${selectedVariant === 'search-hub' ? '!h-8 !w-8 -mt-8 !mx-2' : ''}`"
      loop
      :show-arrows="$viewport.lg && (!title || selectedVariant === 'search-hub')"
      :show-arrows-on-hover-only="$viewport.lg && (!title || selectedVariant === 'search-hub')"
      :size-controls="selectedVariant === 'search-hub' ? 'sm' : 'md'"
      :slides-to-scroll="!$viewport.md ? 'auto' : (variant === '6-cards' ? 6 : 3)"
    >
      <template v-if="!products?.length">
        <div
          v-for="i in 6"
          :key="i"
          v-style:w="variantStyleMap[selectedVariant]"
          class="pr-1 children:skeleton"
        >
          <div style="aspect-ratio: 0.72" />
        </div>
      </template>
      <template v-else>
        <div
          v-for="(product, i) in products"
          :key="product.id"
          v-style:w="variantStyleMap[selectedVariant]"
          :class="{ 'pr-1': selectedVariant !== 'search-hub' }"
          @click="$emit('product-click', product, i + 1)"
        >
          <product-card-recommended
            :currency="currency"
            v-bind="product"
            :gtm-config="{ list: _list, category: _list }"
            :quickshop="selectedVariant !== 'search-hub' && quickshop"
          />
        </div>
      </template>
    </vf-carousel-elevate>
  </div>
</template>

<script lang="ts" setup>
import type { Responsive } from '#types/common'
import type { LayoutVariant } from '#types/components/cms/recommendation-carousel'
import type { ResponsiveColor } from '#types/content'
import { getPageInfo } from '#commerce/gtm/page'

const {
  quickshop = true,
  title,
  titleStyle = 'body-3',
  variant = '6-cards'
} = defineProps<{
  currency: string
  loading?: boolean
  products: any[]
  quickshop?: boolean
  title?: string
  titleColor?: ResponsiveColor
  titleHtml?: string
  titleStyle?: string
  variant?: LayoutVariant
}>()

defineEmits<{
  'product-click': [product: any, position: number]
}>()

const { brandClasses } = useAppConfig().components.cms.productRecommendations
const carouselRef = ref()
const route = useRoute()
const [pageCategory] = getPageInfo(route, {})

const variantOverride = inject('variantOverride', undefined) as LayoutVariant | undefined

const variantStyleMap: Record<LayoutVariant, Responsive> = {
  '3-cards': { sm: '74.66%', md: '29.49%', lg: '29.58%' },
  '5-cards': { sm: '40%', md: '28%', lg: '22%' },
  '6-cards': { sm: '74.66%', md: '28%', lg: '18%' },
  'search-hub': { sm: '15.625rem', md: '15.625rem', lg: '15.625rem' }
}

const selectedVariant = computed(() => variantOverride || variant)

const _list = getRecommendationListDescription({ list: `${pageCategory || 'Home'} | Product Recommendation Carousel`, title })
</script>
